<script setup lang="ts" generic="T extends object">
  import { computed, ref, reactive, useSlots } from "vue";
  import Draggable from "vuedraggable";
  import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
  import { Skeleton } from "@/components/ui/skeleton";
  import { ArrowDown, ArrowUp, ArrowUpDown, GripVertical } from "lucide-vue-next";
  import { Pagination } from "@/components/ui/pagination";
  import DataTableRow from "./DataTableRow.vue";
  import type { DataTableColumn } from "./types";

  const DRAG_OPTIONS = { animation: 150, ghostClass: "opacity-40" };

  const props = withDefaults(
    defineProps<{
      items: T[];
      columns: DataTableColumn<T>[];
      currentPage?: number;
      totalPages?: number;
      itemsPerPage?: number;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
      loading?: boolean;
      emptyMessage?: string;
      itemKey?: keyof T | string;
      resizable?: boolean;
      // Opt-in: apply the columns' declared `width` even without `resizable`.
      fixedWidths?: boolean;
      selectedItemId?: string | number | null;
      skeletonRows?: number;
      tableClass?: string;
      height?: string;
      variant?: "default" | "compact" | "dense";
      clickableRows?: boolean;
      rowDataCy?: string;
      actionsClass?: string;
      expandedKeys?: (string | number)[];
      // Opt-in drag and drop: columns flagged `reorderable` move by their header grip; rows move by
      // a grip in their first cell, a predicate keeps some rows (totals, summaries) in place. Rows are
      // never reorderable while `expandedKeys` is set.
      reorderableColumns?: boolean;
      reorderableRows?: boolean | ((item: T) => boolean);
    }>(),
    {
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 20,
      sortColumn: "",
      sortDirection: "asc",
      loading: false,
      emptyMessage: "No items found.",
      itemKey: "id",
      resizable: false,
      fixedWidths: false,
      selectedItemId: null,
      skeletonRows: 10,
      tableClass: "",
      height: "auto",
      variant: "default",
      clickableRows: false,
      rowDataCy: undefined,
      actionsClass: "w-[70px]",
      expandedKeys: undefined,
      reorderableColumns: false,
      reorderableRows: false,
    },
  );

  const emit = defineEmits<{
    sort: [column: string];
    "page-change": [page: number];
    "row-click": [item: T, event: MouseEvent | KeyboardEvent];
    "reorder-columns": [keys: string[]];
    "reorder-rows": [items: T[]];
  }>();

  const expandable = computed(() => props.expandedKeys !== undefined);
  const isExpanded = (item: T, index: number) => props.expandedKeys?.includes(getItemKey(item, index)) ?? false;
  const rowsReorderable = computed(() => !!props.reorderableRows && !expandable.value);
  const rowDraggable = (item: T) =>
    rowsReorderable.value && (typeof props.reorderableRows === "function" ? props.reorderableRows(item) : true);
  const rowHandle = (item: T) => (rowsReorderable.value ? (rowDraggable(item) ? "visible" : "hidden") : "none");
  const columnDraggable = (column: DataTableColumn<T>) => props.reorderableColumns && column.reorderable === true;
  const columnCount = computed(() => visibleColumns.value.length + (expandable.value ? 1 : 0) + (slots.actions ? 1 : 0));
  const rowState = (item: T, index: number) => {
    if (isExpanded(item, index)) return "open";
    return props.selectedItemId != null && getItemKey(item, index) === props.selectedItemId ? "selected" : undefined;
  };

  const onColumnsMoved = (columns: DataTableColumn<T>[]) => emit("reorder-columns", columns.map((column) => column.key));
  const columnMove = (event: { draggedContext: { element: DataTableColumn<T> }; relatedContext: { element: DataTableColumn<T> } }) =>
    columnDraggable(event.draggedContext.element) && columnDraggable(event.relatedContext.element);
  const rowMove = (event: { draggedContext: { element: T }; relatedContext: { element: T } }) =>
    rowDraggable(event.draggedContext.element) && rowDraggable(event.relatedContext.element);

  const slots = useSlots();
  const visibleColumns = computed(() => props.columns.filter((col) => col.visible !== false));

  const getItemKey = (item: T, index: number): string | number => {
    if (props.itemKey && props.itemKey in item) {
      return item[props.itemKey as keyof T] as string | number;
    }
    return index;
  };

  const sortIcon = (column: DataTableColumn<T>) => {
    if (props.sortColumn !== column.key) return ArrowUpDown;
    return props.sortDirection === "asc" ? ArrowUp : ArrowDown;
  };

  const getCellValue = (item: T, column: DataTableColumn<T>): unknown => {
    if (column.render) {
      return column.render(item);
    }
    return item[column.key as keyof T];
  };

  // Column resizing
  const resizing = ref(false);
  const columnWidths = reactive<Record<string, number>>({});

  const getColumnWidth = (column: DataTableColumn<T>): number => {
    if (columnWidths[column.key]) {
      return columnWidths[column.key];
    }
    return column.width || 150;
  };

  const getColumnStyle = (column: DataTableColumn<T>) => {
    if (props.resizable) {
      const width = getColumnWidth(column);
      return { width: `${width}px`, minWidth: `${width}px` };
    }
    if (props.fixedWidths && column.width) return { width: `${column.width}px`, minWidth: `${column.width}px` };
    return {};
  };

  const startResize = (event: MouseEvent, columnKey: string) => {
    if (!props.resizable) return;
    event.preventDefault();
    resizing.value = true;

    const startX = event.clientX;
    const column = visibleColumns.value.find((c) => c.key === columnKey);
    const startWidth = getColumnWidth(column!);

    const onMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startX;
      columnWidths[columnKey] = Math.max(50, startWidth + diff);
    };

    const onMouseUp = () => {
      resizing.value = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  defineExpose({ resizing });
</script>

<template>
  <div>
    <div
      class="overflow-auto"
      :class="[
        variant === 'compact' && 'bg-white [&_td]:py-2 [&_td]:text-xs [&_td]:font-medium [&_th]:text-xs',
        variant === 'default' && 'bg-white border rounded-md',
        variant === 'dense' &&
          '[&_th]:h-auto [&_th]:bg-muted [&_th]:py-2.5 [&_th]:px-3 [&_th]:text-3xs [&_th]:font-medium [&_th]:uppercase [&_th]:tracking-[0.1em] [&_th]:text-foreground-subtle [&_thead_tr]:border-y [&_thead_tr]:border-secondary [&_td:not([data-slot=detail])]:px-3 [&_td:not([data-slot=detail])]:py-3.5 [&_td]:text-13 [&_td_button]:text-13 [&_tbody_tr]:border-secondary [&_tbody_tr>td]:bg-background [&_tbody_tr:not([data-state=open]):hover>td]:bg-muted [&_tbody_tr:hover>td:first-child]:rounded-l-lg [&_tbody_tr:hover>td:last-child]:rounded-r-lg [&_tbody_tr[data-state=selected]>td]:bg-muted [&_tbody_tr[data-state=selected]>td:first-child]:rounded-l-lg [&_tbody_tr[data-state=selected]>td:last-child]:rounded-r-lg',
        clickableRows && '[&_tbody_tr]:cursor-pointer',
        { 'select-none': resizing },
      ]"
      :style="{ height }">
      <Table
        class="text-sm"
        :class="[resizable || fixedWidths ? 'table-fixed' : '', tableClass]">
        <TableHeader :class="variant === 'compact' ? 'sticky top-0 z-10 bg-white' : variant === 'dense' ? '[&_tr]:hover:bg-transparent' : ''">
          <Draggable
            tag="tr"
            class="border-b transition-colors hover:bg-muted/50"
            :model-value="visibleColumns"
            item-key="key"
            handle=".column-handle"
            :disabled="!reorderableColumns"
            :move="columnMove"
            v-bind="DRAG_OPTIONS"
            @update:model-value="onColumnsMoved">
            <template #header>
              <TableHead
                v-if="expandable"
                class="w-9 sticky top-0 z-10"
                :class="variant === 'dense' ? 'bg-transparent' : 'bg-white'" />
            </template>
            <template #item="{ element: column }">
            <TableHead
              class="relative group select-none sticky top-0 z-10"
              :class="[
                variant === 'dense' ? 'bg-transparent' : 'bg-white',
                column.headerClass,
                column.align === 'right' && 'text-right',
                column.sortable ? (variant === 'dense' ? 'cursor-pointer hover:text-foreground-secondary' : 'cursor-pointer hover:bg-muted/50') : '',
              ]"
              :style="getColumnStyle(column)"
              @click="column.sortable ? emit('sort', column.key) : undefined">
              <template v-if="loading">
                <Skeleton class="h-4 w-20" />
              </template>
              <template v-else>
                <div
                  class="flex items-center gap-1 [&>[role=checkbox]]:translate-y-[2px]"
                  :class="column.align === 'right' && 'justify-end'">
                  <GripVertical
                    v-if="columnDraggable(column)"
                    class="column-handle size-3.5 shrink-0 cursor-grab text-foreground-subtle opacity-0 group-hover:opacity-100 active:cursor-grabbing" />
                  <span
                    v-if="column.label"
                    class="line-clamp-1">
                    {{ column.label }}
                  </span>
                  <component
                    :is="sortIcon(column)"
                    v-if="column.sortable"
                    class="shrink-0"
                    :class="
                      variant === 'dense'
                        ? ['size-3', sortColumn === column.key ? 'text-foreground-secondary' : 'opacity-40']
                        : 'ml-2 h-4 w-4'
                    " />
                  <slot
                    :name="`header-${column.key}-actions`"
                    :column="column" />
                </div>
              </template>
              <div
                v-if="resizable && column.resizable !== false"
                class="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-primary/50 group-hover:bg-border"
                @mousedown="startResize($event, column.key)" />
            </TableHead>
            </template>
            <template #footer>
              <TableHead
                v-if="$slots.actions"
                class="sticky top-0 z-10"
                :class="[actionsClass, variant === 'dense' ? 'bg-transparent' : 'bg-white']">
                <slot name="actions-header" />
              </TableHead>
            </template>
          </Draggable>
        </TableHeader>
        <TableBody v-if="loading">
          <TableRow
            v-for="i in skeletonRows"
            :key="`skeleton-${i}`">
            <TableCell
              v-for="column in visibleColumns"
              :key="`skeleton-${i}-${column.key}`"
              :style="getColumnStyle(column)">
              <Skeleton class="h-4 w-full" />
            </TableCell>
            <TableCell v-if="$slots.actions">
              <Skeleton class="h-4 w-8" />
            </TableCell>
          </TableRow>
        </TableBody>
        <TableBody v-else-if="items.length === 0">
          <TableRow class="hover:bg-transparent">
            <TableCell
              :colspan="columnCount"
              class="text-center text-muted-foreground py-8">
              <slot name="empty">{{ emptyMessage }}</slot>
            </TableCell>
          </TableRow>
        </TableBody>
        <Draggable
          v-else-if="rowsReorderable"
          tag="tbody"
          class="[&_tr:last-child]:border-0"
          :model-value="items"
          :item-key="(item: T) => getItemKey(item, items.indexOf(item))"
          handle=".row-handle"
          :move="rowMove"
          v-bind="DRAG_OPTIONS"
          @update:model-value="emit('reorder-rows', $event)">
          <template #item="{ element: item, index }">
            <DataTableRow
              :item="item"
              :columns="visibleColumns"
              :state="rowState(item, index)"
              :expandable="false"
              :handle="rowHandle(item)"
              :clickable="clickableRows"
              :row-data-cy="rowDataCy"
              :column-style="getColumnStyle"
              :cell-value="getCellValue"
              @click="emit('row-click', item, $event)">
              <template
                v-for="name in Object.keys($slots)"
                :key="name"
                #[name]="slotProps">
                <slot
                  :name="name"
                  v-bind="slotProps ?? {}" />
              </template>
            </DataTableRow>
          </template>
        </Draggable>
        <TableBody v-else>
          <template
            v-for="(item, index) in items"
            :key="getItemKey(item, index)">
            <DataTableRow
              :item="item"
              :columns="visibleColumns"
              :state="rowState(item, index)"
              :expandable="expandable"
              handle="none"
              :clickable="clickableRows"
              :row-data-cy="rowDataCy"
              :column-style="getColumnStyle"
              :cell-value="getCellValue"
              @click="emit('row-click', item, $event)">
              <template
                v-for="name in Object.keys($slots)"
                :key="name"
                #[name]="slotProps">
                <slot
                  :name="name"
                  v-bind="slotProps ?? {}" />
              </template>
            </DataTableRow>
            <TableRow
              v-if="expandable && isExpanded(item, index)"
              class="bg-background hover:bg-background">
              <TableCell
                data-slot="detail"
                :colspan="columnCount"
                class="p-0">
                <slot
                  name="expanded"
                  :item="item" />
              </TableCell>
            </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>

    <Pagination
      v-if="totalPages > 1"
      :total="totalPages * itemsPerPage"
      :items-per-page="itemsPerPage"
      :default-page="currentPage"
      @update:page="emit('page-change', $event)" />
  </div>
</template>
